// ============================================================================
// Cosmetic asset registry — single source of truth for background/audio URLs.
// Files in this folder are swappable; update manifest.json to match.
// ============================================================================

import type { AppTheme, CanvasBackgroundKey, FocusSoundscapeKey } from '../../types/schema'

import blueprintGrid from './backgrounds/blueprint-grid.svg'
import nebula from './backgrounds/nebula.svg'
import circuit from './backgrounds/circuit.svg'

export interface BackgroundAsset {
  preferenceKey: CanvasBackgroundKey
  cosmeticId: string
  name: string
  url: string
  /** Repeat tile size in px — matches the SVG viewBox. */
  tilingPx: number
}

export const BACKGROUND_ASSETS: BackgroundAsset[] = [
  { preferenceKey: 'BLUEPRINT_GRID', cosmeticId: 'bg_grid', name: 'Blueprint Grid', url: blueprintGrid, tilingPx: 220 },
  { preferenceKey: 'NEBULA', cosmeticId: 'bg_nebula', name: 'Nebula', url: nebula, tilingPx: 600 },
  { preferenceKey: 'CIRCUIT', cosmeticId: 'bg_circuit', name: 'Circuit', url: circuit, tilingPx: 260 },
]

export interface SoundscapeAsset {
  preferenceKey: FocusSoundscapeKey
  cosmeticId: string
  name: string
  /** null = no audio (Silence) or the file has not been provided yet. */
  url: string | null
}

// Audio files are intentionally absent until real assets are produced.
export const SOUNDSCAPE_ASSETS: SoundscapeAsset[] = [
  { preferenceKey: 'SILENCE', cosmeticId: 'audio_silence', name: 'Silence', url: null },
  { preferenceKey: 'LOFI_RAIN', cosmeticId: 'lofi_rain_vinyl', name: 'Lo-Fi Heavy Rain + Vinyl Surface Crackle', url: null },
  { preferenceKey: 'ALPHA_WAVE', cosmeticId: 'alpha_singing_bowl', name: 'Alpha-Wave Pad + High-Resonance Singing Bowl', url: null },
]

const BG_BY_KEY = new Map(BACKGROUND_ASSETS.map((a) => [a.preferenceKey, a]))
const BG_BY_COSMETIC = new Map(BACKGROUND_ASSETS.map((a) => [a.cosmeticId, a]))
const SOUND_BY_KEY = new Map(SOUNDSCAPE_ASSETS.map((a) => [a.preferenceKey, a]))
const SOUND_BY_COSMETIC = new Map(SOUNDSCAPE_ASSETS.map((a) => [a.cosmeticId, a]))

export const getBackgroundByKey = (key: CanvasBackgroundKey) => BG_BY_KEY.get(key)
export const getBackgroundByCosmeticId = (id: string) => BG_BY_COSMETIC.get(id)
export const getSoundscapeByKey = (key: FocusSoundscapeKey) => SOUND_BY_KEY.get(key)
export const getSoundscapeByCosmeticId = (id: string) => SOUND_BY_COSMETIC.get(id)

export const BACKGROUND_COSMETIC_TO_KEY: Record<string, CanvasBackgroundKey> = {
  bg_grid: 'BLUEPRINT_GRID',
  bg_nebula: 'NEBULA',
  bg_circuit: 'CIRCUIT',
}

export const SOUNDSCAPE_COSMETIC_TO_KEY: Record<string, FocusSoundscapeKey> = {
  audio_silence: 'SILENCE',
  lofi_rain_vinyl: 'LOFI_RAIN',
  alpha_singing_bowl: 'ALPHA_WAVE',
}

export const THEME_COSMETIC_TO_KEY = {
  theme_midnight: 'MIDNIGHT',
  theme_aurora: 'AURORA',
  theme_ember: 'EMBER',
  theme_celestial: 'CELESTIAL',
  theme_pearl: 'PEARL',
  theme_linen: 'LINEN',
  theme_glacier: 'GLACIER',
  theme_blush: 'BLUSH',
  theme_sage: 'SAGE',
  theme_light: 'LIGHT',
  theme_dark: 'DARK',
  theme_oled_black: 'OLED_BLACK',
} as const

export const THEME_KEY_TO_COSMETIC_ID: Partial<Record<AppTheme, string>> = {
  MIDNIGHT: 'theme_midnight',
  AURORA: 'theme_aurora',
  EMBER: 'theme_ember',
  CELESTIAL: 'theme_celestial',
  PEARL: 'theme_pearl',
  LINEN: 'theme_linen',
  GLACIER: 'theme_glacier',
  BLUSH: 'theme_blush',
  SAGE: 'theme_sage',
  LIGHT: 'theme_light',
  DARK: 'theme_dark',
  OLED_BLACK: 'theme_oled_black',
}

export const ORBIT_SKIN_COSMETIC_TO_KEY = {
  orbit_cream: 'cream',
  orbit_ivory: 'ivory',
  orbit_glass: 'glass',
  orbit_ink: 'ink',
  orbit_outline: 'outline',
} as const

export type OrbitSkinCosmeticId = keyof typeof ORBIT_SKIN_COSMETIC_TO_KEY
